import { CheckOutlined, SwapOutlined } from '@ant-design/icons';
import { Card, Pagination, Radio, Spin, Timeline, Typography } from 'antd';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { CandidateId, InterviewHistoryChangedFields } from '../../types';
import ApplicantAdmissionSentTimeline from './ApplicantAdmissionSentTimeline';
import ApplicantAnnotationTimeline from './ApplicantAnnotationTimeline';
import ApplicantAnnotationWithStatusTimeline from './ApplicantAnnotationWithStatusTimeline';
import ApplicantGivenUpTimeline from './ApplicantGivenUpTimeline';
import ApplicantHiredTimeline from './ApplicantHiredTimeline';
import ApplicantInterviewEventTimeline from './ApplicantInterviewEventTimeline';
import ApplicantMessageTimeline from './ApplicantMessageTimeline';
import ApplicantStageMovimentationTimeline from './ApplicantStageMovimentationTimeline';

const ApplicantHistory = () => {
  const { applicant_id } = useTypedParams({ applicant_id: CandidateId });
  const [searchParams, setSearchParams] = useSearchParams();

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const historyFilters = searchParams.get('historyFilters') || undefined;

  const userPages = useInfiniteAPIQuery(['/users/', { params: { is_active: true } }]);

  const users = usePrefetchPages(userPages, (data) => data.results);

  const candidateHistory = useAPIQuery([
    '/candidate-history/',
    {
      params: {
        candidate: applicant_id,
        type__in: historyFilters,
        page: parseInt(searchParams.get('historyPage') || '1', 10),
        page_size: parseInt(searchParams.get('historyPageSize') || '10', 10),
      },
    },
  ]);

  return (
    <ErrorResult error={candidate.error || candidateHistory.error}>
      <Spin spinning={candidate.isLoading || candidateHistory.isLoading}>
        <Helmet>
          <title>Histórico | {candidate.data?.name || '...'} | Recrutamento e Seleção</title>
        </Helmet>
        <Radio.Group
          buttonStyle="solid"
          value={
            searchParams.get('historyFilters') ||
            'JOB_APPLICATION,CANDIDATE_SELECTION,STAGE_MOVIMENTATION,RECRUITMENT_PROCESS_STATUS_EVENT,COMMENTARY,EMAIL,INTERVIEW_EVENT'
          }
          onChange={(e) => {
            searchParams.set('historyFilters', e.target.value);
            searchParams.set('historyPage', '1');
            setSearchParams(searchParams);
          }}
        >
          <Radio.Button value="JOB_APPLICATION,CANDIDATE_SELECTION,STAGE_MOVIMENTATION,RECRUITMENT_PROCESS_STATUS_EVENT,COMMENTARY,EMAIL,DIGITAL_ADMISSION,INTERVIEW_EVENT">
            Tudo
          </Radio.Button>
          <Radio.Button value="JOB_APPLICATION" data-testid="job-applications">
            Candidaturas
          </Radio.Button>
          <Radio.Button value="CANDIDATE_SELECTION,STAGE_MOVIMENTATION,DIGITAL_ADMISSION">
            Movimentações nas etapas
          </Radio.Button>
          <Radio.Button value="RECRUITMENT_PROCESS_STATUS_EVENT">Status nos processos</Radio.Button>
          <Radio.Button value="COMMENTARY">Comentários</Radio.Button>
          <Radio.Button value="EMAIL">E-mails</Radio.Button>
          <Radio.Button value="INTERVIEW_EVENT">Entrevistas</Radio.Button>
        </Radio.Group>
        <br />
        <br />
        <Timeline data-testid="candidate-timeline">
          {candidateHistory.data?.results.map((candidateHistoryEntry, index) => (
            <div key={index}>
              {candidateHistoryEntry.type === 'JOB_APPLICATION' && (
                <Timeline.Item dot={<CheckOutlined />}>
                  <Card>
                    <Typography.Text type="secondary">
                      {moment(candidateHistoryEntry.created_at).format('LLL')}
                    </Typography.Text>
                    <br />
                    <Typography.Text>
                      Pessoa candidata {candidate.data?.name} se inscreveu na vaga{' '}
                      <Typography.Text strong>
                        {candidateHistoryEntry.job_posting?.name || '-'}
                      </Typography.Text>
                    </Typography.Text>
                  </Card>
                </Timeline.Item>
              )}
              {candidateHistoryEntry.type === 'CANDIDATE_SELECTION' && (
                <Timeline.Item dot={<SwapOutlined />}>
                  <Card>
                    <Typography.Text type="secondary">
                      {moment(candidateHistoryEntry.created_at).format('LLL')}
                    </Typography.Text>
                    <br />
                    <Typography.Text>
                      <Typography.Text strong>
                        {candidateHistoryEntry.created_by?.is_staff
                          ? 'Sistema'
                          : candidateHistoryEntry.created_by?.full_name || '-'}
                      </Typography.Text>{' '}
                      triou o(a) candidato(a) na vaga{' '}
                      <Typography.Text strong>
                        {candidateHistoryEntry.job_posting?.name || '-'}
                      </Typography.Text>
                    </Typography.Text>
                  </Card>
                </Timeline.Item>
              )}
              {candidateHistoryEntry.type === 'STAGE_MOVIMENTATION' &&
                candidateHistoryEntry.to_stage !== 'Contratados' && (
                  <ApplicantStageMovimentationTimeline
                    createdDate={candidateHistoryEntry.created_at}
                    fromStage={candidateHistoryEntry.from_stage || '-'}
                    toStage={candidateHistoryEntry.to_stage || '-'}
                    opportunityTitle={candidateHistoryEntry.job_posting?.name || '-'}
                    recruiterName={
                      candidateHistoryEntry.created_by?.is_staff
                        ? 'Sistema'
                        : candidateHistoryEntry.created_by?.full_name || '-'
                    }
                  />
                )}
              {candidateHistoryEntry.type === 'STAGE_MOVIMENTATION' &&
                candidateHistoryEntry.to_stage === 'Contratados' && (
                  <ApplicantHiredTimeline
                    createdDate={candidateHistoryEntry.created_at}
                    recruiterName={
                      candidateHistoryEntry.created_by?.is_staff
                        ? 'Sistema'
                        : candidateHistoryEntry.created_by?.full_name || '-'
                    }
                    candidateId={candidateHistoryEntry.candidate}
                    opportunityId={candidateHistoryEntry.job_posting?.id}
                    opportunityTitle={candidateHistoryEntry.job_posting?.name}
                  />
                )}
              {candidateHistoryEntry.type === 'RECRUITMENT_PROCESS_STATUS_EVENT' &&
                candidateHistoryEntry.recruitment_process_status_event !== 'GIVEN_UP' && (
                  <ApplicantAnnotationWithStatusTimeline
                    annotation={candidateHistoryEntry.commentary || undefined}
                    createdDate={candidateHistoryEntry.created_at}
                    recruiterName={
                      candidateHistoryEntry.created_by?.is_staff
                        ? 'Sistema'
                        : candidateHistoryEntry.created_by?.full_name || '-'
                    }
                    status={candidateHistoryEntry.recruitment_process_status_event || 'REACTIVATED'}
                    opportunityTitle={candidateHistoryEntry.job_posting?.name || '-'}
                  />
                )}
              {candidateHistoryEntry.type === 'RECRUITMENT_PROCESS_STATUS_EVENT' &&
                candidateHistoryEntry.recruitment_process_status_event === 'GIVEN_UP' && (
                  <ApplicantGivenUpTimeline
                    createdDate={candidateHistoryEntry.created_at}
                    recruiterName={
                      candidateHistoryEntry.created_by?.is_staff
                        ? 'Sistema'
                        : candidateHistoryEntry.created_by?.full_name || '-'
                    }
                    opportunityTitle={candidateHistoryEntry.job_posting?.name || '-'}
                    comment={candidateHistoryEntry.commentary || undefined}
                  />
                )}
              {candidateHistoryEntry.type === 'COMMENTARY' && (
                <ApplicantAnnotationTimeline
                  fromStage={candidateHistoryEntry.from_stage || undefined}
                  opportunityTitle={candidateHistoryEntry.job_posting?.name}
                  annotation={candidateHistoryEntry.commentary || ''}
                  users={users}
                  annotationType={candidateHistoryEntry.commentary_type || undefined}
                  createdDate={candidateHistoryEntry.created_at}
                  recruiterName={
                    candidateHistoryEntry.created_by?.is_staff
                      ? 'Sistema'
                      : candidateHistoryEntry.created_by?.full_name || '-'
                  }
                />
              )}
              {candidateHistoryEntry.type === 'EMAIL' &&
                candidateHistoryEntry.email_communication && (
                  <ApplicantMessageTimeline
                    createdAt={candidateHistoryEntry.created_at}
                    recruiterName={
                      candidateHistoryEntry.created_by?.is_staff
                        ? 'Sistema'
                        : candidateHistoryEntry.created_by?.full_name
                    }
                    opportunityTitle={candidateHistoryEntry.job_posting?.name}
                    emailCommunication={candidateHistoryEntry.email_communication}
                  />
                )}
              {candidateHistoryEntry.type === 'DIGITAL_ADMISSION' && (
                <ApplicantAdmissionSentTimeline
                  createdDate={candidateHistoryEntry.created_at}
                  recruiterName={
                    candidateHistoryEntry.created_by?.is_staff
                      ? 'Sistema'
                      : candidateHistoryEntry.created_by?.full_name || '-'
                  }
                  candidateId={candidateHistoryEntry.candidate}
                  opportunityId={candidateHistoryEntry.job_posting?.id}
                  opportunityTitle={candidateHistoryEntry.job_posting?.name}
                />
              )}
              {candidateHistoryEntry.type === 'INTERVIEW_EVENT' && (
                <ApplicantInterviewEventTimeline
                  createdAt={candidateHistoryEntry.created_at}
                  recruiterName={
                    candidateHistoryEntry.created_by.is_staff
                      ? 'Sistema'
                      : candidateHistoryEntry.created_by.full_name || '-'
                  }
                  opportunityTitle={candidateHistoryEntry.job_posting.name}
                  atStage={candidateHistoryEntry.at_stage || '-'}
                  interviewHistoryEventType={candidateHistoryEntry.interview_history_type}
                  interviewHistoryChangedFields={
                    candidateHistoryEntry.interview_history_changed_fields as InterviewHistoryChangedFields
                  }
                />
              )}
            </div>
          ))}
        </Timeline>
        <Pagination
          onChange={(page, pageSize) => {
            searchParams.set('historyPage', page.toString());
            searchParams.set('historyPageSize', pageSize.toString());
            setSearchParams(searchParams);
          }}
          current={parseInt(searchParams.get('historyPage') || '1', 10)}
          total={candidateHistory.data?.count || 0}
          pageSize={parseInt(searchParams.get('historyPageSize') || '10', 10)}
        />
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(ApplicantHistory);
